import React, { useContext, useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { InitDomain } from "src/context/domain/domainContext";
import {
  Alert,
  Box,
  Button,
  Form,
  Multiselect,
  SpaceBetween,
  Table,
} from "@amzn/awsui-components-console";
import { FullPageTitle } from "src/components/header/title";
import { isProd } from "src/util";
import {
  S3Client,
  ListObjectsV2Command,
  ListObjectsV2CommandOutput,
  GetObjectCommand,
  GetObjectCommandOutput,
} from "@aws-sdk/client-s3";
import { STSClient, AssumeRoleCommand } from "@aws-sdk/client-sts";
import Header from "@amzn/awsui-components-console/header";
import Container from "@amzn/awsui-components-console/container";

export type LoadingStatus = "loading" | "finished" | "error" | "unauthorized";

export const AppJemValidation: React.FC = () => {
  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("loading");
  const [s3Client, setS3Client] = useState<S3Client | null>(null);
  const postfix = !isProd() ? "beta-fe" : "prod-fe";
  const [s3FileOptions, setS3FileOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [reports, setReports] = useState<Array<{ key: string; data: any }>>([]);
  const [expandedItems, setExpandedItems] = useState<any[]>([]);

  const { authService } = useContext(InitDomain);

  useEffect(() => {
    const fetchData = async () => {
      // Run authentication
      await authService.authenticate();

      // Load ARD data by default on component mount
      if (
        authService.getIsLoggedIn() &&
        (authService.ldap.includes("bart-jemgen-admin") ||
          authService.ldap.includes("bart-read-only") ||
          authService.ldap.includes("bart-admin"))
      ) {
        getS3Files();
      } else {
        setLoadingStatus("unauthorized");
      }
    };
    fetchData();
  }, []);

  const getS3Files = async () => {
    setLoadingStatus("loading");
    try {
      const awsAccount = !isProd() ? "886436970742" : "495599731039";
      const credentials = await Auth.currentCredentials();
      const essentialCredentials = Auth.essentialCredentials(credentials);

      // Create STS client to assume role
      const stsClient = new STSClient({
        region: "us-west-2",
        credentials: essentialCredentials,
      });

      const roleArn = `arn:aws:iam::${awsAccount}:role/FacdS3JemValidationBucketAccessRole-${postfix}`;
      const assumeRoleCommand = new AssumeRoleCommand({
        RoleArn: roleArn,
        RoleSessionName: "JemValidationS3Access",
      });

      const assumeRoleResponse = await stsClient.send(assumeRoleCommand);
      const assumedCredentials = assumeRoleResponse.Credentials;

      if (!assumedCredentials) {
        throw new Error("Failed to assume role");
      }

      const s3Client = new S3Client({
        region: "us-west-2",
        credentials: {
          accessKeyId: assumedCredentials.AccessKeyId!,
          secretAccessKey: assumedCredentials.SecretAccessKey!,
          sessionToken: assumedCredentials.SessionToken!,
        },
      });
      setS3Client(s3Client);

      const listBucketCommand = new ListObjectsV2Command({
        Bucket: `jem-validation-${postfix}`,
      });

      const response: ListObjectsV2CommandOutput =
        await s3Client.send(listBucketCommand);
      const options =
        response.Contents?.map((obj) => ({
          label: obj.Key!,
          value: obj.Key!,
        })) || [];
      setS3FileOptions(options);
      setLoadingStatus("finished");
    } catch (error) {
      console.error(
        "An error occurred in listBucketCommand for S3Client: ",
        error,
      );
      setLoadingStatus("error");
    } finally {
      console.log("ListBucket operation completed.");
    }
  };

  const fetchReportFiles = async () => {
    if (!s3Client) {
      console.error("S3 client not initialized");
      return;
    }
    setLoadingStatus("loading");
    try {
      const fetchedReports = [];

      for (const option of selectedOptions) {
        const response: GetObjectCommandOutput = await s3Client.send(
          new GetObjectCommand({
            Bucket: `jem-validation-${postfix}`,
            Key: option.value,
          }),
        );
        const body = await response.Body?.transformToString();
        fetchedReports.push({
          key: option.value,
          data: JSON.parse(body || "{}"),
        });
      }

      setReports(fetchedReports.sort((a, b) => a.key.localeCompare(b.key)));
      setLoadingStatus("finished");
    } catch (error) {
      console.error("Error fetching report files:", error);
      setLoadingStatus("error");
    }
  };

  if (loadingStatus === "unauthorized") {
    return (
      <div>
        <Box margin="xxl">
          <Alert header="Access denied!" type="error"></Alert>
        </Box>
      </div>
    );
  }

  return (
    <>
      <FullPageTitle
        title={`JemValidation${!isProd() ? " (Beta)" : ""}`}
        description={"Jem Validation Report(s) Viewing Tool"}
      />
      <SpaceBetween size="l">
        <form onSubmit={(e) => e.preventDefault()}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" onClick={fetchReportFiles}>
                  {selectedOptions.length === 2
                    ? "Fetch Reports"
                    : "Fetch Report"}
                </Button>
              </SpaceBetween>
            }
          >
            <Container
              header={
                <Header variant="h2">Jem Validation Report Viewer</Header>
              }
            >
              <SpaceBetween direction="vertical" size="l">
                <Multiselect
                  selectedOptions={selectedOptions || []}
                  onChange={({ detail }) =>
                    setSelectedOptions(detail.selectedOptions)
                  }
                  options={s3FileOptions
                    .sort((a, b) => b.label.localeCompare(a.label))
                    .map((option) => ({
                      ...option,
                      disabled:
                        selectedOptions.length >= 2 &&
                        !selectedOptions.some((s) => s.value === option.value),
                    }))}
                  placeholder="Select report(s)"
                />
              </SpaceBetween>
            </Container>
          </Form>
        </form>

        {reports.length > 0 &&
          (() => {
            const tableItems = Array.from(
              new Set(reports.flatMap((r) => Object.keys(r.data))),
            ).map((ardId) => ({
              id: ardId,
              ardId,
              level: "ard",
              useCases: reports.reduce((acc, report) => {
                const ardData = report.data[ardId];
                if (ardData) {
                  Object.entries(ardData).forEach(
                    ([uslKey, uslData]: [string, any]) => {
                      const useCase = uslData?.useCase;
                      const uslLine = uslData?.uslLine;
                      if (useCase && uslLine !== undefined) {
                        if (!acc[useCase]) acc[useCase] = {};
                        if (!acc[useCase][uslLine]) acc[useCase][uslLine] = [];
                        acc[useCase][uslLine].push({
                          reportKey: report.key,
                          data: uslData,
                        });
                      }
                    },
                  );
                }
                return acc;
              }, {}),
            }));

            return (
              <Container>
                <Table
                  trackBy="id"
                  columnDefinitions={[
                    {
                      id: "ardInformation",
                      header: "ARD Information",
                      cell: (item) =>
                        item.ardId ||
                        item.useCase ||
                        (item.uslLine ? `USL Line ${item.uslLine}` : ""),
                    },
                    {
                      id: "report1",
                      header: `${reports[0]?.key || "Report 1"} (Report 1)`,
                      cell: (item) => {
                        if (item.level === "usl") {
                          const violations =
                            item.violations?.filter(
                              (v) => v.reportKey === reports[0].key,
                            ) || [];
                          if (violations.length === 0) return "✅";
                          return (
                            <div>
                              {violations.map((v, idx) => (
                                <div key={idx}>
                                  Violation:{" "}
                                  {v.data?.message || JSON.stringify(v.data)}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        if (item.level === "usecase") {
                          const count = Object.values(item.uslLines || {})
                            .flat()
                            .filter(
                              (v: any) => v.reportKey === reports[0].key,
                            ).length;
                          return count > 0
                            ? `❌ ${count} violations`
                            : "✅ 0 violations";
                        }
                        const total = Object.values(item.useCases || {}).reduce(
                          (sum, uc: any) =>
                            sum +
                            Object.values(uc)
                              .flat()
                              .filter(
                                (v: any) => v.reportKey === reports[0].key,
                              ).length,
                          0,
                        );
                        return total > 0
                          ? `❌ ${total} violations`
                          : "✅ 0 violations";
                      },
                    },
                    ...(reports.length === 2
                      ? [
                          {
                            id: "report2",
                            header: `${reports[1]?.key || "Report 2"} (Report 2)`,
                            cell: (item) => {
                              if (item.level === "usl") {
                                const violations =
                                  item.violations?.filter(
                                    (v) => v.reportKey === reports[1].key,
                                  ) || [];
                                if (violations.length === 0) return "✅";
                                return (
                                  <div>
                                    {violations.map((v, idx) => (
                                      <div key={idx}>
                                        Violation:{" "}
                                        {v.data?.message ||
                                          JSON.stringify(v.data)}
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              if (item.level === "usecase") {
                                const count = Object.values(item.uslLines || {})
                                  .flat()
                                  .filter(
                                    (v: any) => v.reportKey === reports[1].key,
                                  ).length;
                                return count > 0
                                  ? `❌ ${count} violations`
                                  : "✅ 0 violations";
                              }
                              const total = Object.values(
                                item.useCases || {},
                              ).reduce(
                                (sum, uc: any) =>
                                  sum +
                                  Object.values(uc)
                                    .flat()
                                    .filter(
                                      (v: any) =>
                                        v.reportKey === reports[1].key,
                                    ).length,
                                0,
                              );
                              return total > 0
                                ? `❌ ${total} violations`
                                : "✅ 0 violations";
                            },
                          },
                          {
                            id: "comparison",
                            header: "New Violations (Report 2 vs Report 1)",
                            cell: (item) => {
                              if (item.level === "usl") {
                                const report1Violations =
                                  item.violations?.filter(
                                    (v) => v.reportKey === reports[0].key,
                                  ) || [];
                                const report2Violations =
                                  item.violations?.filter(
                                    (v) => v.reportKey === reports[1].key,
                                  ) || [];
                                const report1Messages = new Set(
                                  report1Violations.map((v) => v.data?.message),
                                );
                                const newViolations = report2Violations.filter(
                                  (v) => !report1Messages.has(v.data?.message),
                                );
                                if (newViolations.length === 0)
                                  return "✅ No new violations";
                                return (
                                  <div>
                                    {newViolations.map((v, idx) => (
                                      <div key={idx}>
                                        New:{" "}
                                        {v.data?.message ||
                                          JSON.stringify(v.data)}
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              if (item.level === "usecase") {
                                const report1Count = Object.values(
                                  item.uslLines || {},
                                )
                                  .flat()
                                  .filter(
                                    (v: any) => v.reportKey === reports[0].key,
                                  ).length;
                                const report2Count = Object.values(
                                  item.uslLines || {},
                                )
                                  .flat()
                                  .filter(
                                    (v: any) => v.reportKey === reports[1].key,
                                  ).length;
                                const diff = report2Count - report1Count;
                                if (diff > 0)
                                  return `❌ +${diff} new violations`;
                                if (diff < 0)
                                  return `✅ ${Math.abs(diff)} violations fixed`;
                                return "✅ No new violations";
                              }
                              const report1Total = Object.values(
                                item.useCases || {},
                              ).reduce(
                                (sum, uc: any) =>
                                  sum +
                                  Object.values(uc)
                                    .flat()
                                    .filter(
                                      (v: any) =>
                                        v.reportKey === reports[0].key,
                                    ).length,
                                0,
                              );
                              const report2Total = Object.values(
                                item.useCases || {},
                              ).reduce(
                                (sum, uc: any) =>
                                  sum +
                                  Object.values(uc)
                                    .flat()
                                    .filter(
                                      (v: any) =>
                                        v.reportKey === reports[1].key,
                                    ).length,
                                0,
                              );
                              const diff = report2Total - report1Total;
                              if (diff > 0) return `❌ +${diff} new violations`;
                              if (diff < 0)
                                return `✅ ${Math.abs(diff)} violations fixed`;
                              return "✅ No new violations";
                            },
                          },
                        ]
                      : []),
                  ]}
                  items={tableItems}
                  variant="container"
                  expandableRows={{
                    getItemChildren: (item) => {
                      if (item.level === "usecase") {
                        const children = Object.entries(
                          item.uslLines || {},
                        ).map(([uslLine, violations]) => ({
                          id: `${item.id}-${uslLine}`,
                          uslLine,
                          violations,
                          level: "usl",
                        }));

                        return children;
                      }
                      const children = Object.entries(item.useCases || {}).map(
                        ([useCase, uslLines]) => ({
                          id: `${item.id}-${useCase}`,
                          useCase,
                          uslLines,
                          level: "usecase",
                        }),
                      );
                      return children;
                    },
                    isItemExpandable: (item) => {
                      if (item.level === "usecase") {
                        const expandable =
                          Object.keys(item.uslLines || {}).length > 0;
                        return expandable;
                      }
                      if (item.level === "usl") {
                        return false;
                      }
                      const expandable =
                        Object.keys(item.useCases || {}).length > 0;
                      return expandable;
                    },
                    expandedItems,
                    onExpandableItemToggle: (event) => {
                      const { item, expanded } = event.detail;
                      setExpandedItems((prev) => {
                        if (expanded) {
                          return [...prev, item];
                        }
                        return prev.filter((i) => i.id !== item.id);
                      });
                    },
                  }}
                  loadingText="Loading reports"
                  empty="No reports fetched"
                />
              </Container>
            );
          })()}
      </SpaceBetween>
    </>
  );
};

export default AppJemValidation;
